// Personalisation copy for plan comparison and upgrade prompts.
// Shared by Onboarding Step 6, /upgrade, and gated-feature prompts.

export type EnrolBand = "under_25" | "25_50" | "51_100" | "over_100";
export type CurrentSystem = "paper" | "other_software" | "mix" | "nothing";

export function lineForChildren(band?: string | null): string | null {
  switch (band) {
    case "under_25":
      return "Your nursery is within the Starter plan limit. You can start free and upgrade at any time.";
    case "25_50":
      return "Your nursery has between 25 and 50 children: Starter covers up to 25. Professional gives you unlimited capacity from day one.";
    case "51_100":
    case "over_100":
      return "With 50+ children, Starter is not the right fit. Professional is built for nurseries your size.";
    default:
      return null;
  }
}

export function lineForPainPoint(point?: string | null): string | null {
  switch (point) {
    case "dbs_first_aid":
      return "You mentioned DBS management is a headache. Professional tracks every expiry and alerts you automatically, 60 and 30 days before they lapse.";
    case "rota":
      return "You mentioned the staff rota takes time every week. Professional includes rota export, actual hours tracking and payroll-ready reports.";
    case "ofsted":
      return "You mentioned Ofsted preparation is stressful. Professional gives you a live readiness score and exports a full audit pack in one click.";
    case "attendance_funding":
      return "You mentioned attendance and funding tracking. Professional gives you full attendance reports and funding management in one place.";
    case "register":
      return "You mentioned room registers. Professional gives every staff member a personal digital register view, sign in from any device.";
    case "payroll":
      return "You mentioned payroll is painful. Professional tracks planned and actual hours per shift and exports a payroll-ready CSV.";
    case "pickups":
      return "You mentioned after school pickup coordination. Professional includes a full pickup and drop-off module across all your schools.";
    default:
      return null;
  }
}

export function lineForSystem(system?: string | null): string | null {
  switch (system) {
    case "other_software":
    case "mix":
      return "Since you are already on a platform, our free migration means we move all your records across for you, no manual re-entry.";
    case "paper":
      return "Moving from paper? Our team migrates your existing records into Nursly as part of your Professional plan, completely free.";
    case "nothing":
      return "Starting fresh means you can build it right from day one. Professional gives you all the tools to run a fully compliant nursery.";
    default:
      return null;
  }
}

// Per-feature personalised line for the gated-feature upgrade prompt.
export function lineForFeature(feature: string): string {
  const map: Record<string, string> = {
    Compliance:
      "Track every certificate and get alerts before anything lapses: DBS, First Aid, qualifications, all in one place.",
    "Ofsted Readiness":
      "See your live readiness score and export a full audit pack in one click.",
    "Attendance & Staffing":
      "Full attendance reports and funding tracking: everything you need for funding claims and parent conversations.",
    Reports:
      "Rota hours, attendance rates, zone occupancy: every report your nursery needs in one place.",
    "Audit Log":
      "A complete tamper-proof record of every action on your platform, essential for regulatory compliance.",
    "Pickups & Drop-offs":
      "Manage after school pickup bookings, school lists and tick-offs in one place.",
    Funding:
      "Track 15 and 30 hour entitlements, monitor renewals and stay ahead of expiring codes.",
    "Alerts & Reminders":
      "Automated DBS, certificate and staffing alerts so nothing slips through the cracks.",
  };
  return map[feature] ?? `${feature} is part of the Professional plan.`;
}

/**
 * The single canonical list of what Professional includes, shown in the
 * "See all features" modal and on the upgrade page. Keep these two in step.
 */
export const PROFESSIONAL_FEATURES: string[] = [
  "Live ratio compliance monitoring, staff on break excluded automatically",
  "Digital risk assessments, all 9 required types, with manager sign-off",
  "Fire safety compliance, drill scheduling and equipment tracking",
  "Accident, incident, and complaints logging",
  "One-click Ofsted audit pack, every document ready in seconds",
  "Live Ofsted readiness score across every inspection category",
  "DBS and paediatric first aid tracking with expiry alerts",
  "Medicine administration log with parental consent",
  "Parents and staff sign in at the door, synced to your rota and register",
  "After school and holiday club pickup coordination",
  "Additional day requests, sent directly to parents",
  "Six-year audit trail",
  "Free data migration",
];

/** Annual costs ten monthly payments, with no annual per-month equivalent. */
export const ANNUAL_SAVING_LINE = "2 months free";
